import { QueryKey, useQuery } from '@tanstack/react-query';
import { getBooking } from '@hqo/service-booking-client';
import { BookingCalendarOptions } from '@hqo/service-booking-types';

import { useApi } from 'app/shared/contexts/api';
import { useServiceBookingAuth } from 'app/shared/hooks/use-service-booking-auth';
import { useUser } from 'app/shared/contexts/user';
import { retryOnAuthError } from 'app/shared/auth';

export const BOOKING_CALENDAR_OPTIONS_QUERY_KEY = 'booking-calendar-options';

// The BE presigns the .ics for 24h; refetching hourly keeps an open screen's URL live.
const REFRESH_INTERVAL_MS = 60 * 60 * 1000;
// Past this age the cached .ics URL may be dead (e.g. a long-backgrounded tab), so it is dropped.
const ICS_MAX_AGE_MS = 23 * REFRESH_INTERVAL_MS;
// The result modal opens before the BE settles the booking to CONFIRMED (incl. a paid charge), so an empty answer is re-polled for up to 30s.
export const SETTLE_POLL_MS = 1500;
export const SETTLE_MAX_POLLS = 20;

export const getUseBookingCalendarOptionsQueryKey = (
  eventUuid: string | undefined,
  bookingUuid: string | undefined,
  userUuid: string | undefined,
): QueryKey => [BOOKING_CALENDAR_OPTIONS_QUERY_KEY, userUuid ?? '', eventUuid ?? '', bookingUuid ?? ''];

interface UseBookingCalendarOptionsQueryParams {
  eventUuid?: string;
  bookingUuid?: string;
}

/**
 * Add-to-calendar URLs from the booking detail response. `null` when the BE
 * omits them (booking holds no live seat) or the fetch fails — the button hides.
 * `icsDownloadUrl` is dropped once the cached copy nears the presign TTL.
 */
export const useBookingCalendarOptionsQuery = ({
  eventUuid,
  bookingUuid,
}: UseBookingCalendarOptionsQueryParams): BookingCalendarOptions | null => {
  const { apiUrl } = useApi();
  const getAuthOptions = useServiceBookingAuth();
  const { user } = useUser();

  const { data, dataUpdatedAt } = useQuery<BookingCalendarOptions | null, Error>({
    queryKey: getUseBookingCalendarOptionsQueryKey(eventUuid, bookingUuid, user?.uuid),
    queryFn: async () => {
      const auth = await getAuthOptions();
      const result = await getBooking(
        { baseUrl: apiUrl, eventUuid: eventUuid ?? '', bookingUuid: bookingUuid ?? '' },
        auth,
      );
      if (!result.isSuccess()) throw result.errorData;

      return result.value.calendarOptions ?? null;
    },
    enabled: !!eventUuid && !!bookingUuid && !!apiUrl && !!user?.uuid,
    staleTime: REFRESH_INTERVAL_MS,
    refetchInterval: ({ state }) => {
      const isSettling = state.data === null && state.dataUpdateCount < SETTLE_MAX_POLLS;

      return isSettling ? SETTLE_POLL_MS : REFRESH_INTERVAL_MS;
    },
    retry: retryOnAuthError,
  });

  if (!data) return null;
  if (Date.now() - dataUpdatedAt < ICS_MAX_AGE_MS) return data;

  const { icsDownloadUrl: _expired, ...deepLinks } = data;

  return deepLinks;
};
